import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ScrollToTop from "@/components/ScrollToTop";
import { ThemeProvider } from "@/components/ThemeProvider";

import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import ProtectedRoute from "@/components/ProtectedRoute";
import PageLoader from "@/components/PageLoader";
import CookieBanner from "@/components/CookieBanner";
import "@/components/CookieBanner"; // Ensure it's bundled
import { KeepAlive } from "@/components/KeepAlive";
import { ContactPopup } from "@/components/ContactPopup";

// Lazy Imports - Core

const Landing = lazy(() => import("@/pages/Landing"));
const Auth = lazy(() => import("@/pages/Auth"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const CompleteRegistration = lazy(() => import("@/pages/CompleteRegistration"));
const ConfirmEmail = lazy(() => import("@/pages/ConfirmEmail"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const CookiePolicy = lazy(() => import("@/pages/CookiePolicy"));
const Termini = lazy(() => import("@/pages/Termini"));
const Contatti = lazy(() => import("@/pages/Contatti"));
const GuidaForfettario = lazy(() => import("@/pages/GuidaForfettario"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const ChiSiamo = lazy(() => import("@/pages/ChiSiamo"));
const ComeFunziona = lazy(() => import("@/pages/ComeFunziona"));

// Calculators
const CalcoloArtigiani = lazy(() => import("@/pages/CalcoloArtigiani"));
const CalcoloCommercianti = lazy(() => import("@/pages/CalcoloCommercianti"));
const CalcoloProfessionisti = lazy(() => import("@/pages/CalcoloProfessionisti"));
const CoefficientiRedditivita = lazy(() => import("@/pages/CoefficientiRedditivita"));
const FatturazioneElettronica = lazy(() => import("@/pages/FatturazioneElettronica"));
const ScadenzeFiscali = lazy(() => import("@/pages/ScadenzeFiscali"));
const ConfrontoRegimi = lazy(() => import("@/pages/ConfrontoRegimi"));
const AperturaPartitaIva = lazy(() => import("@/pages/AperturaPartitaIva"));

// Commercialisti
const CommercialistaMonza = lazy(() => import("@/pages/CommercialistaMonza"));
const CommercialistaPavia = lazy(() => import("@/pages/CommercialistaPavia"));
const CommercialistaCorsico = lazy(() => import("@/pages/CommercialistaCorsico"));
const CommercialistaMeda = lazy(() => import("@/pages/CommercialistaMeda"));
const CommercialistaForfettari = lazy(() => import("@/pages/CommercialistaForfettari"));
const CommerciallistaDitteIndividuali = lazy(() => import("@/pages/CommerciallistaDitteIndividuali"));
const CommerciallistaSocieta = lazy(() => import("@/pages/CommerciallistaSocieta"));
const ChiediInformazioni = lazy(() => import("@/pages/ChiediInformazioni"));
const Sitemap = lazy(() => import("@/pages/Sitemap"));

// Blog
const Blog = lazy(() => import("@/pages/Blog"));
const ContributiInpsForfettari = lazy(() => import("@/pages/blog/ContributiInpsForfettari"));
const FlatTaxForfettari = lazy(() => import("@/pages/blog/FlatTaxForfettari"));
const LimitiFatturatoForfettario = lazy(() => import("@/pages/blog/LimitiFatturatoForfettario"));
const ComeFatturareForfettario = lazy(() => import("@/pages/blog/ComeFatturareForfettario"));
const DeduzioniDetrazioniForfettari = lazy(() => import("@/pages/blog/DeduzioniDetrazioniForfettari"));
const CalcoloTasseForfettario = lazy(() => import("@/pages/blog/CalcoloTasseForfettario"));
const AccontiInps2026 = lazy(() => import("@/pages/blog/AccontiInps2026"));
const QuantoPagoTassePartitaIva = lazy(() => import("@/pages/blog/QuantoPagoTassePartitaIva"));

// Demo & App

const Subscription = lazy(() => import("@/pages/Subscription"));
const Index = lazy(() => import("@/pages/Index"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <TooltipProvider>
        <KeepAlive />
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <ScrollToTop />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/complete-registration" element={<CompleteRegistration />} />
                <Route path="/confirm-email" element={<ConfirmEmail />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/termini" element={<Termini />} />
                <Route path="/contatti" element={<Contatti />} />
                <Route path="/guida-regime-forfettario" element={<GuidaForfettario />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/chi-siamo" element={<ChiSiamo />} />
                <Route path="/come-funziona" element={<ComeFunziona />} />
                <Route path="/calcolo-artigiani" element={<CalcoloArtigiani />} />
                <Route path="/calcolo-commercianti" element={<CalcoloCommercianti />} />
                <Route path="/calcolo-professionisti" element={<CalcoloProfessionisti />} />
                <Route path="/coefficienti-redditivita" element={<CoefficientiRedditivita />} />
                <Route path="/fatturazione-elettronica" element={<FatturazioneElettronica />} />
                <Route path="/scadenze-fiscali" element={<ScadenzeFiscali />} />
                <Route path="/confronto-regimi" element={<ConfrontoRegimi />} />
                <Route path="/aprire-partita-iva" element={<AperturaPartitaIva />} />
                <Route path="/commercialista-monza-brianza" element={<CommercialistaMonza />} />
                <Route path="/commercialista-pavia" element={<CommercialistaPavia />} />
                <Route path="/commercialista-corsico" element={<CommercialistaCorsico />} />
                <Route path="/commercialista-meda" element={<CommercialistaMeda />} />
                <Route path="/commercialista-forfettari" element={<CommercialistaForfettari />} />
                <Route path="/commercialista-ditte-individuali" element={<CommerciallistaDitteIndividuali />} />
                <Route path="/commercialista-societa" element={<CommerciallistaSocieta />} />
                <Route path="/chiedi-informazioni" element={<ChiediInformazioni />} />
                <Route path="/mappa-sito" element={<Sitemap />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/contributi-inps-forfettari" element={<ContributiInpsForfettari />} />
                <Route path="/blog/flat-tax-forfettari" element={<FlatTaxForfettari />} />
                <Route path="/blog/limiti-fatturato-forfettario" element={<LimitiFatturatoForfettario />} />
                <Route path="/blog/come-fatturare-forfettario" element={<ComeFatturareForfettario />} />
                <Route path="/blog/deduzioni-detrazioni-forfettari" element={<DeduzioniDetrazioniForfettari />} />
                <Route path="/blog/calcolo-tasse-forfettario" element={<CalcoloTasseForfettario />} />
                <Route path="/blog/acconti-inps-2026" element={<AccontiInps2026 />} />
                <Route path="/blog/quanto-pago-tasse-partita-iva" element={<QuantoPagoTassePartitaIva />} />

                <Route path="/calcolatori" element={<Navigate to="/app" replace />} />
                <Route
                  path="/subscription"
                  element={
                    <ProtectedRoute>
                      <Subscription />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/app"
                  element={
                    <ProtectedRoute requireSubscription>
                      <Index />
                    </ProtectedRoute>
                  }
                />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AuthProvider>
          <CookieBanner />
          <ContactPopup />
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
